<template>
  <div>
    <h1>Abonnement</h1>

    <!-- Show ended message if tenancy is not active -->
    <div v-if="!isActive" class="tenancy-ended-message">
      <div class="ended-content">
        <h3>Leieforholdet er avsluttet</h3>
        <p>Abonnementsiden er ikke tilgjengelig for avsluttede leieforhold.</p>
      </div>
    </div>

    <!-- Show normal subscription content when tenancy is active -->
    <template v-else>

      <!-- Pending cancelation -->
      <div v-if="getTenancySubscription?.pendingCancelation" class="subscription-disclaimer">
        <div class="disclaimer-content">
          <div class="disclaimer-text">
            <h5>Kommende avslutning</h5>
            <p>
              Sluttdato: <em>{{ formatDate(getTenancySubscription?.pendingCancelation?.endDate) }}</em>
            </p>
            <p>
              Begrunnelse: <em>{{
                getCancellationReasonText(getTenancySubscription?.pendingCancelation?.cancellationReason) }}</em>
            </p>
            <p class="mt-3">Du kan avbryte denne avslutningen hvis ønskelig.</p>
          </div>
        </div>
        <button class="btn btn-primary mt-4" @click="cancelPendingChanges" :disabled="isCancelingPending">
          <i v-if="isCancelingPending" class="budicon-loading spinner"></i>
          {{ isCancelingPending ? 'Avbryter...' : 'Avbryt avslutning' }}
        </button>
      </div>

      <!-- Pending change -->
      <div v-else-if="getTenancySubscription?.pendingChange" class="subscription-disclaimer">
        <div class="disclaimer-content">
          <div class="disclaimer-text">
            <h5>Kommende endringer</h5>
            <p>
              Endringsdato: <em>{{ formatDate(getTenancySubscription?.pendingChange?.changeDate) }}</em>
            </p>
            <p>
              Fra: <em>{{ subscriptionPlan || 'Ukjent' }}</em>
            </p>
            <p>
              Til: <em>{{ getPlanNameById(getTenancySubscription?.pendingChange?.newSubscriptionPlanId) }}</em>
            </p>
            <p class="mt-3">Du kan avbryte denne endringen hvis ønskelig.</p>
          </div>
        </div>
        <button class="btn btn-primary mt-4" @click="cancelPendingChanges" :disabled="isCancelingPending">
          <i v-if="isCancelingPending" class="budicon-loading spinner"></i>
          {{ isCancelingPending ? 'Avbryter...' : 'Avbryt endringer' }}
        </button>
      </div>


      <TenancySubscriptionPlanPreview v-if="!isFree" :plan="tenancySubscriptionData[subscriptionPlan]"
        :tenancy-data="getTenancy" :orders="orders" :buttons="[
          {
            text: 'Endre abonnement',
            classes: 'btn-outline-secondary',
            disabled: false,
            func: toggleCancelSubscriptionModal
          }
        ]" />
      <div v-else class="upgrade-cards-container">
        <p>Velg det abonnementet som passer best for dine behov:</p>
        <div class="upgrade-cards">
          <!-- Basic Card -->
          <TenancySubscriptionSinglePlanCard :plan="{ name: 'Basic', price: '99' }"
            :bullet-points="tenancySubscriptionData['Basic'].features" @upgrade="openUpgradeModal('Basic')" />

          <!-- Premium Card -->
          <TenancySubscriptionSinglePlanCard :plan="{ name: 'Premium', price: '199' }"
            :bullet-points="tenancySubscriptionData['Premium'].features" @upgrade="openUpgradeModal('Premium')" />
        </div>
      </div>
      <hl-Modal v-if="showCancelSubscription" dismisable @close="toggleCancelSubscriptionModal"
        :title="cancelPage === 0 ? cancelModalConfig.actionSelection.title : ''">
        <CancelSubscriptionModalRenderer :current-page="cancelPage" :config="cancelModalConfig"
          :form-data="cancelFormData" :is-loading="isLoading" :tenancy-subscription-data="tenancySubscriptionData"
          :subscription-plan="subscriptionPlan" :tenant-name="getTenancy?.tenants?.[0]?.fullName || ''"
          :subscription-name="tenancySubscriptionData[subscriptionPlan]?.title || ''" :orders="orders"
          :tenancy-subscription="getTenancySubscription" :effective-date-text="getEffectiveDate()"
          :invoice-status-text="getInvoiceStatus()" @page-change="changeCancelPage"
          @form-update="updateCancelFormData" @action-submit="handleCancelModalSubmit"
          @modal-close="toggleCancelSubscriptionModal" />
      </hl-Modal>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from 'vuex';
import { TenancyApi, TenancyAccountingApi } from '@modules/husleieapi2';
import i18nCommon from '@modules/i18n/mixins/Common';
import subscription from '@/mixins/tenancy/subscription';
import RadioButton from '@modules/husleie-framework/src/components/forms/inputs/radio-buttons/RadioButton.vue'
import Checkbox from '@modules/husleie-framework/src/components/forms/inputs/checkbox/Checkbox';
import TenancySubscriptionPlanPreview from '@/components/tenancy/TenancySubscriptionPlanPreview.vue';
import TenancySubscriptionSinglePlanCard from '@/components/TenancySubscriptionSinglePlanCard.vue';
import CancelSubscriptionModalRenderer from '@/components/modal/CancelSubscriptionModalRenderer.vue';
import cancelModalConfig from '@/constants/cancelModalConfig.js';
import {
  getLatestIssuedAccountingOrder,
  getOrderReferenceDate,
  getScheduledDowngradeDateFromOrders,
  getScheduledDowngradeDateISO,
  isValidPendingChangeDate,
} from '@/lib/tenancySubscriptionDates';

moment.locale('nb');

export default {
  ORDER_LIMIT: 24,
  mixins: [i18nCommon, subscription],
  components: {
    RadioButton,
    Checkbox,
    TenancySubscriptionPlanPreview,
    TenancySubscriptionSinglePlanCard,
    CancelSubscriptionModalRenderer,
  },

  props: {
    tenancyId: {
      type: [String, Number],
      required: true
    },
    landlordId: {
      type: [String, Number],
      required: true
    },
  },

  data() {
    return {
      planHierarchy: { 'Free': 0, 'Basic': 1, 'Premium': 2 },
      subscriptionPlan: '',
      plans: [],
      orders: [],

      isLoading: false,
      isCancelingPending: false,

      selectedPlanName: '',
      isUpdatingSubscription: false,

      showCancelSubscription: false,
      cancelPage: 0,

      cancelModalConfig,

      cancelFormData: {
        selectedCancelAction: '',
        selectedTenancyEndDate: '',
        selectedNewSubscription: '',
        isDowngradeFlow: false,
        cancelReason: '',
        otherReason: '',
        allowPlanChange: false,
        selectedPlanChange: '',
        originalSubscriptionPlan: ''
      },

      hasPendingCancellation: false,
      pendingCancellationEndDate: '',

      tenancySubscriptionData: {
        'Free': {
          title: 'Gratis',
          subtitle: 'Grunnleggende funksjoner',
          iconClass: 'budicon-free',
          highlighted: false,
          selected: false,
          isFree: true,
          price: '0',
          active: false,
          features: [
            { text: 'Leiekontrakt m/ BankID', active: true },
            { text: 'Overtakelsesprotokoll', active: true },
            { text: 'Oppsigelse og heving', active: true },
            { text: 'Utleieannonse', active: true },
          ]
        },
        'Basic': {
          title: 'Basic',
          subtitle: 'for de fleste utleiere',
          iconClass: 'budicon-mansion',
          highlighted: false,
          selected: false,
          isFree: false,
          price: '99',
          active: false,
          features: [
            { text: 'Alt i Gratis', active: true },
            { text: 'Fakturering av husleien', active: true },
            { text: 'Betalingsoppfølging', active: true },
            { text: 'Bistand til utkastelse', active: true },
            { text: 'Depositumskonto', active: true },
            { text: 'Telefonsupport', active: true },
          ]
        },
        'Premium': {
          title: 'Premium',
          subtitle: '',
          iconClass: 'budicon-business-area',
          highlighted: false,
          selected: false,
          isFree: false,
          price: '199',
          active: false,
          features: [
            { text: 'Alt i Basic', active: true },
            { text: 'Indeksregulering', active: true },
            { text: 'Utleieforsikring', active: true },
            { text: 'Advokatbistand (1 time per år)', active: true },
          ]
        },
      },
    };
  },
  async created() {
    await Promise.all([
      this.getTenancyPlan(),
      this.fetchAvailablePlans(),
      this.fetchOrders()
    ]).catch(err => {
      console.error('Failed to load subscription data:', err);
    });
  },
  computed: {
    ...mapGetters('tenancy', ['getTenancySubscriptionPlan', 'getTenancySubscription', 'getTenancy', 'getTenancyStatus']),
    ...mapGetters(['getLandlordProfilesMeta']),
    latestIssuedOrder() {
      return getLatestIssuedAccountingOrder(this.orders);
    },
    latestIssuedOrderDate() {
      return getOrderReferenceDate(this.latestIssuedOrder);
    },
    isFree() {
      return this.subscriptionPlan === 'Free' || !this.subscriptionPlan
    },
    isActive() {
      // TODO: Are there more active-statuses?
      const activeStatuses = ['active', 'in-cancellation']
      if (!activeStatuses.includes(this.getTenancyStatus)) {
        return false
      }

      // Check if tenancy has ended based on endDate
      const tenancy = this.getTenancy;
      if (tenancy && tenancy.endDate) {
        const endDate = moment(tenancy.endDate);
        const today = moment();
        if (endDate.isValid() && endDate.isBefore(today)) {
          return false;
        }
      }

      return true
    },
    isOwnedSubscription() {
      return this.subscriptionPlan === this.selectedPlanName
    },
  },
  methods: {
    ...mapActions('tenancy', [
      'updateTenancy',
      'fetchTenancyAccountingOrders',
      'fetchTenancy'
    ]),
    async refreshSubscriptionState() {
      await this.fetchTenancy({ landlordId: this.landlordId, tenancyId: this.tenancyId });
      this.getTenancyPlan();
    },

    async fetchOrders() {
      const api = new TenancyAccountingApi();

      try {
        this.orders = await api.getLandlordTenancyAccountingOrders(
          this.landlordId,
          this.tenancyId,
          {
            query: {
              limit: this.$options.ORDER_LIMIT,
              page: 1,
            },
          }
        );
      } catch {
        this.$toast('Kunne ikke hente fakturaer.', 'error');
      }
    },
    getTenancyPlan() {
      this.subscriptionPlan = this.getTenancySubscriptionPlan
      this.selectedPlanName = this.subscriptionPlan

      // Check for pending cancellation
      const subscription = this.getTenancySubscription;
      if (subscription && subscription.pendingCancelation && subscription.pendingCancelation.endDate) {
        this.hasPendingCancellation = true;
        this.pendingCancellationEndDate = subscription.pendingCancelation.endDate;
      }

      if (!this.isFree) {
        // Clear any previous selections
        Object.keys(this.tenancySubscriptionData).forEach(planKey => {
          this.tenancySubscriptionData[planKey].selected = false;
        });

        // Set current plan as selected and active
        if (this.subscriptionPlan && this.tenancySubscriptionData[this.subscriptionPlan]) {
          this.tenancySubscriptionData[this.subscriptionPlan].selected = true;
          this.tenancySubscriptionData[this.subscriptionPlan].active = true;
        }
      }
    },
    formatDate(t) {
      let d = moment(t).format("DD.MM.YYYY");
      return (d === 'Invalid date') ? '-' : d;
    },
    toggleCancelSubscriptionModal() {
      this.cancelPage = 0;
      this.cancelFormData = {
        selectedCancelAction: '',
        selectedTenancyEndDate: '',
        selectedNewSubscription: '',
        isDowngradeFlow: false,
        cancelReason: '',
        otherReason: '',
        allowPlanChange: false,
        selectedPlanChange: '',
        originalSubscriptionPlan: this.subscriptionPlan
      };
      this.showCancelSubscription = !this.showCancelSubscription;
    },
    openUpgradeModal(planName) {
      // Pre-populate form data for upgrade flow
      this.cancelFormData = {
        selectedCancelAction: 'terminate_only',
        selectedTenancyEndDate: '',
        selectedNewSubscription: planName,
        isDowngradeFlow: false,
        cancelReason: '',
        otherReason: '',
        allowPlanChange: false,
        selectedPlanChange: '',
        originalSubscriptionPlan: this.subscriptionPlan
      };

      // Start at confirmation page (page 2)
      this.cancelPage = 2;
      this.showCancelSubscription = true;
    },
    async onEndDateSubmit() {
      this.isLoading = true;

      try {
        // Update tenancy enddate
        const tenancyPayload = {
          ...this.getTenancy,
          endDate: this.cancelFormData.selectedTenancyEndDate
        };

        await this.updateTenancy({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: tenancyPayload,
        });

        this.cancelPage = 4;
      } catch (error) {
        console.error("Failed to update tenancy using updateTenancy:", error);
        this.$toast('Kunne ikke sette sluttdato.', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    async onCancelSubscriptionSubmit() {
      this.isLoading = true;

      try {
        const api = new TenancyApi();

        let targetPlan = this.cancelFormData.selectedNewSubscription;

        if (this.cancelFormData.allowPlanChange && 
            this.cancelFormData.selectedPlanChange && 
            this.cancelFormData.selectedNewSubscription !== 'Free') {
          targetPlan = this.cancelFormData.selectedPlanChange;
        }

        if (!targetPlan) {
          console.error('No plan selected');
          this.$toast('Vennligst velg et abonnement', 'error');
          this.isLoading = false;
          return;
        }

        if (targetPlan === 'Free') {
          // Handle cancellation to Free plan
          const cancellationDate = this.getCancellationDateISO({ fallbackToCurrentMonthEnd: true });
          const requestBody = {
            cancelationReason: this.cancelFormData.isDowngradeFlow
              ? (this.cancelFormData.cancelReason === 'other' ? this.cancelFormData.otherReason : this.cancelFormData.cancelReason)
              : 'subscription_change',
          };

          if (cancellationDate) {
            requestBody.subscriptionEndDate = cancellationDate;
          }

          await api.pendingCancelTenancySubscription(this.landlordId, this.tenancyId, {
            body: requestBody,
          });
        } else {
          // Handle plan change (Basic/Premium)
          const plan = this.plans.find(plan => plan.name === targetPlan);
          if (!plan) {
            console.error(`No plan found with name: ${targetPlan}. Available plans:`, this.plans);
            this.$toast(`Fant ikke "${targetPlan}" abonnement`, 'error');
            this.isLoading = false;
            return;
          }

          if (this.isUpgrade(targetPlan, this.subscriptionPlan)) {
            await api.upgradeTenancySubscription(this.landlordId, this.tenancyId, {
              body: {
                subscriptionPlanId: plan.id,
                origin: 'tenancy_subscription',
              },
            });

            await this.refreshSubscriptionState();
          } else {
            await api.pendingChangeTenancySubscription(this.landlordId, this.tenancyId, {
              body: this.buildPendingChangeBody(plan.id),
            });
          }
        }

        await this.refreshSubscriptionState();

        // Navigate to success page
        if (this.cancelFormData.isDowngradeFlow) {
          this.cancelPage = 4;
        } else {
          this.cancelPage = 3;
        }

      } catch (error) {
        console.error("Error in onCancelSubscriptionSubmit:", error);
        this.$toast('Kunne ikke endre abonnement. Prøv igjen.', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    isUpgrade(newPlan, oldPlan) {
      // If both plans are the same, it's not an upgrade
      if (newPlan === oldPlan) {
        return false
      }
      // If changing subscription from free, its always an upgrade
      if (oldPlan === 'Free' || !oldPlan) {
        return true
      }
      // If changing subscription to Premium, its always an upgrade
      if (newPlan === 'Premium') {
        return true
      }
      return false
    },
    isDowngrade(newPlan, oldPlan) {
      return this.planHierarchy[newPlan] < this.planHierarchy[oldPlan];
    },
    setChangePlan(newPlan) {
      // Clear previous selections
      Object.keys(this.tenancySubscriptionData).forEach(planKey => {
        this.tenancySubscriptionData[planKey].selected = false;
      });

      // Set new selection
      if (this.tenancySubscriptionData[newPlan]) {
        this.tenancySubscriptionData[newPlan].selected = true;
      }

      this.selectedPlanName = newPlan
    },
    async onChangeSubmit() {
      this.isLoading = true;
      const newPlan = this.selectedPlanName
      const plan = this.plans.find(plan => plan.name === newPlan);
      if (!plan) {
        console.error(`No plan found with name: ${newPlan}`);
        this.isLoading = false;
        return;
      }

      if (newPlan === this.subscriptionPlan) {
        this.isLoading = false;
        return
      }

      const oldPlan = this.subscriptionPlan

      try {
        const api = new TenancyApi();
        if (this.isUpgrade(newPlan, oldPlan)) {
          await api.upgradeTenancySubscription(this.landlordId, this.tenancyId, {
            body: {
              subscriptionPlanId: plan.id,
              origin: 'tenancy_subscription',
            },
          });

        } else {
          await api.pendingChangeTenancySubscription(this.landlordId, this.tenancyId, {
            body: this.buildPendingChangeBody(plan.id),
          });
        }

        // Update subscription plan on success
        await this.refreshSubscriptionState();
      } catch (error) {
        this.isLoading = false
        console.error("Error in onChangeSubmit:", error);
        this.$toast('Kunne ikke endre abonnement. Prøv igjen.', 'error');
        return false;
      } finally {
        this.isLoading = false
      }
    },

    async fetchAvailablePlans() {
      try {
        const api = new TenancyApi();
        this.plans = await api.getLandlordTenancyAvailableSubscriptions(
          this.landlordId,
          this.tenancyId,
        );
      } catch (error) {
        console.error('getLandlordTenancyAvailableSubscriptions failed', error);
        return false;
      } finally {
        (this.plans || []).forEach(plan => {
          if (plan.active && this.tenancySubscriptionData[plan.name]) {
            this.tenancySubscriptionData[plan.name].active = true;
          }
        })
      }
    },

    // Config-driven modal methods
    updateCancelFormData(updates) {
      // Use Vue.set for each property to ensure reactivity
      Object.keys(updates).forEach(key => {
        this.$set(this.cancelFormData, key, updates[key]);
      });
    },

    changeCancelPage(pageNumber) {
      this.cancelPage = pageNumber;
    },

    async handleCancelModalSubmit(actionMethod) {
      if (this[actionMethod] && typeof this[actionMethod] === 'function') {
        await this[actionMethod]();
      }
    },

    closeModal() {
      this.showCancelSubscription = false;
    },

    getCancellationDate({ fallbackToCurrentMonthEnd = false } = {}) {
      return getScheduledDowngradeDateFromOrders(this.orders, { fallbackToCurrentMonthEnd });
    },

    getCancellationDateISO(options = {}) {
      return getScheduledDowngradeDateISO(this.orders, options);
    },

    buildPendingChangeBody(subscriptionPlanId) {
      const body = { subscriptionPlanId };
      const subscriptionChangeDate = this.getCancellationDateISO({ fallbackToCurrentMonthEnd: true });
      body.subscriptionChangeDate = subscriptionChangeDate;

      return body;
    },

    getEffectiveDate() {
      if (this.cancelFormData.isDowngradeFlow) {
        const cancellationDate = this.getCancellationDate({ fallbackToCurrentMonthEnd: true });

        if (cancellationDate && isValidPendingChangeDate(cancellationDate)) {
          return this.formatDate(cancellationDate);
        }

        const currentMonthEnd = getScheduledDowngradeDateFromOrders(this.orders, { fallbackToCurrentMonthEnd: true });
        return currentMonthEnd ? this.formatDate(currentMonthEnd) : this.formatDate(new Date());
      }
      return 'Umiddelbart';
    },

    getCurrentPlanLabel() {
      return this.tenancySubscriptionData[this.subscriptionPlan]?.title || this.subscriptionPlan;
    },

    getInvoiceStatus() {
      if (!this.latestIssuedOrderDate) {
        return 'Neste måneds faktura er ikke sendt';
      }

      const latest = new Date(this.latestIssuedOrderDate);
      if (isNaN(latest.getTime())) {
        return 'Neste måneds faktura er ikke sendt';
      }

      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

      // If latest invoice due date is in next month or later, next month's invoice is sent
      if (latest >= nextMonth) {
        return 'Neste måneds faktura er sendt';
      } else {
        return 'Neste måneds faktura er ikke sendt';
      }
    },

    getTodayDate() {
      return this.formatDate(new Date());
    },

    getPlanNameById(planId) {
      const plan = this.plans.find(plan => plan.id === planId);
      return plan ? plan.name : 'Ukjent abonnement';
    },

    async cancelPendingChanges() {
      this.isCancelingPending = true;

      try {
        const api = new TenancyApi();
        await api.cancelPendingChangeTenancySubscription(this.landlordId, this.tenancyId);

        // Show success message
        this.$toast('Ventende endringer er avbrutt', 'success');

        await this.refreshSubscriptionState();

      } catch (error) {
        console.error("Error canceling pending changes:", error);
        this.$toast('Kunne ikke avbryte ventende endringer. Prøv igjen.', 'error');
      } finally {
        this.isCancelingPending = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
// Spinner animation for loading states
.spinner {
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

// Tenancy ended message styling
.tenancy-ended-message {
  .ended-content {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    padding: 16px;
    max-width: 500px;

    h3 {
      margin: 0 0 8px 0;
      font-size: 20px;
    }

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
    }
  }
}

.subscription-disclaimer {
  max-width: 500px;
  margin-bottom: 24px;
  padding: 16px 20px;
  border-radius: 8px;
  background-color: #ecf8f2;
  border: 1px solid $primary;

  .disclaimer-content {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    .disclaimer-text {
      flex: 1;

      strong {
        display: block;
        font-weight: 600;
        margin-bottom: 6px;
        font-size: 16px;
      }

      p {
        margin: 0;
        font-size: 14px;
        line-height: 1.5;
      }
    }
  }
}

textarea {
  width: 100%;
  height: 120px;
  padding: 6px;
}

.horizontal-center {
  margin-left: auto;
  margin-right: auto;
}

.grid {
  display: grid;
  gap: 0.5rem;

  &.grid-cols-3 {
    grid-template-columns: 1fr 1fr 1fr;
  }

  &.grid-cols-2 {
    grid-template-columns: 1fr 1fr;
  }
}

.card {
  border: 1px solid $primary;
  background-color: rgba($primary, 0.1);
  width: fit-content;
  padding: 16px;

}

p.strong {
  font-weight: 600;
  font-size: 18px;
  color: black;
  margin: 0;
}

.subscription-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  .subscription-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 18px;
    color: #1f2937;

    i {
      font-size: 20px;
      color: #6b7280;
    }

    .current-badge {
      background-color: #10b981;
      color: white;
      font-size: 12px;
      font-weight: 500;
      padding: 4px 8px;
      border-radius: 12px;
      margin-left: 8px;
    }
  }

  .subscription-price {
    font-weight: 700;
    font-size: 20px;
    color: #059669;
  }


  .subscription-subtitle {
    color: #6b7280;
    font-size: 14px;
    margin-bottom: 16px;
    font-style: italic;
  }

  .subscription-features {
    .feature-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      font-size: 13px;

      &.active {
        color: #059669;

        i {
          color: #10b981;
        }
      }

      &:not(.active) {
        color: #9ca3af;

        i {
          color: #d1d5db;
        }
      }

      i {
        font-size: 12px;
        flex-shrink: 0;
      }
    }

    .more-features {
      color: #6b7280;
      font-size: 12px;
      font-style: italic;
      margin-top: 8px;
    }
  }
}

// Delete tenancy styling
.danger-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background-color: #fef2f2;
  border: 2px solid #dc2626;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  color: #dc2626;

  i {
    font-size: 24px;
    color: #dc2626;
    flex-shrink: 0;
    margin-top: 2px;
  }

  div {
    flex: 1;

    strong {
      display: block;
      font-weight: 700;
      font-size: 16px;
      margin-bottom: 8px;
    }

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      font-weight: 500;
    }
  }
}

.delete-confirmations {
  margin: 24px 0;
  padding: 20px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  .checkbox-wrapper {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ::v-deep .checkbox-component {
    label {
      font-weight: 500;
      color: #374151;
      cursor: pointer;
      font-size: 14px;
      line-height: 1.5;
    }
  }
}

.error-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
  color: #dc2626;

  i {
    font-size: 16px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }
}

// Upgrade cards styling for Free users
.upgrade-cards-container {
  margin: 40px 0;

  >p {
    color: #6b7280;
    font-size: 16px;
    margin-bottom: 32px;
  }
}

.upgrade-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin: 24px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.upgrade-card {
  position: relative;

  &.highlighted {
    .recommended-badge {
      position: absolute;
      top: -12px;
      right: 20px;
      background: linear-gradient(135deg, #f59e0b, #d97706);
      color: white;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
      z-index: 10;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
